/**
 * Product Accordion Card
 * Collapsible accordion for a single product in multi-item mode.
 * Supports multiple variants per product, each with their own lot entries.
 * Each lot entry has its own unit configuration.
 */

import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, Button, Chip, Paper, alpha, useTheme
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon
} from '@mui/icons-material';
import ProductLotEntry from './ProductLotEntry';

const EMPTY_LOT = {
  palletQty: '', caseQty: '', unitQty: '',
  lotNumber: '', batchNumber: '',
  mfgDate: '', expiryDate: ''
};

function extractSize(name) {
  const match = name?.match(/(\d+(\.\d+)?)/);
  return match ? parseFloat(match[1]) : 0;
}

export default function ProductAccordionCard({
  item,
  itemConfig,
  variants,
  onOpenVariantDialog,
  onOpenConfigForLot,
  onUpdateConfig,
  textFieldSx,
  defaultExpanded
}) {
  const theme = useTheme();
  const cfg = itemConfig || {};

  // Multi-variant support
  const selectedVariantIds = cfg.variantIds || (cfg.variantId ? [cfg.variantId] : []);
  const hasMultiVariants = selectedVariantIds.length > 0;
  const variantLots = cfg.variantLots || {};
  const flatLots = cfg.lots || [{ ...EMPTY_LOT }];

  // Get sorted selected variant objects
  const selectedVariantObjects = variants
    .filter(v => selectedVariantIds.includes(v.id))
    .sort((a, b) => extractSize(b.name) - extractSize(a.name));

  // Calculate total base units using per-lot config
  const calcLotTotal = (lots) => lots.reduce((sum, lot) => {
    const upc = parseInt(lot._unitsPerCase) || 1;
    const cpp = parseInt(lot._casesPerPallet) || 1;
    const pQty = parseInt(lot.palletQty) || 0;
    const cQty = parseInt(lot.caseQty) || 0;
    const uQty = parseInt(lot.unitQty) || 0;
    return sum + (pQty * cpp * upc) + (cQty * upc) + uQty;
  }, 0);

  const totalBaseUnits = hasMultiVariants
    ? Object.values(variantLots).reduce((sum, lots) => sum + calcLotTotal(lots), 0)
    : calcLotTotal(flatLots);

  // Build summary text
  const getSummaryText = () => {
    const parts = [];
    if (hasMultiVariants) {
      parts.push(`${selectedVariantIds.length} variant${selectedVariantIds.length > 1 ? 's' : ''}`);
      const totalLots = Object.values(variantLots).reduce((sum, lots) => sum + lots.length, 0);
      if (totalLots > 0) parts.push(`${totalLots} lot${totalLots > 1 ? 's' : ''}`);
    } else {
      if (flatLots.length > 0) parts.push(`${flatLots.length} lot${flatLots.length > 1 ? 's' : ''}`);
      if (cfg.variantName) parts.push(cfg.variantName);
    }
    if (totalBaseUnits > 0) parts.push(`${totalBaseUnits.toLocaleString()} units`);
    return parts.join(' · ') || 'No lots configured';
  };

  // Check completeness — each lot must have its own config selected
  const checkLotsComplete = (lots) => lots.every(lot => {
    if (!lot._configId) return false;
    const upc = parseInt(lot._unitsPerCase) || 1;
    const cpp = parseInt(lot._casesPerPallet) || 1;
    const pQty = parseInt(lot.palletQty) || 0;
    const cQty = parseInt(lot.caseQty) || 0;
    const uQty = parseInt(lot.unitQty) || 0;
    const lotTotal = (pQty * cpp * upc) + (cQty * upc) + uQty;
    return lotTotal > 0 && lot.lotNumber && lot.batchNumber && lot.mfgDate && lot.expiryDate;
  });

  const isComplete = hasMultiVariants
    ? selectedVariantIds.every(vid => {
        const vl = variantLots[vid];
        return vl && vl.length > 0 && checkLotsComplete(vl);
      })
    : flatLots.length > 0 && checkLotsComplete(flatLots);

  // ── Flat lot handlers (no variants) ──
  const handleLotChange = (lotIndex, updatedLot) => {
    const newLots = [...flatLots];
    newLots[lotIndex] = updatedLot;
    onUpdateConfig(item.id, { ...cfg, lots: newLots });
  };
  const handleAddLot = () => {
    onUpdateConfig(item.id, { ...cfg, lots: [...flatLots, { ...EMPTY_LOT }] });
  };
  const handleRemoveLot = (lotIndex) => {
    const newLots = flatLots.filter((_, i) => i !== lotIndex);
    onUpdateConfig(item.id, { ...cfg, lots: newLots.length > 0 ? newLots : [{ ...EMPTY_LOT }] });
  };

  // ── Per-variant lot handlers ──
  const handleVariantLotChange = (variantId, lotIndex, updatedLot) => {
    const vLots = [...(variantLots[variantId] || [{ ...EMPTY_LOT }])];
    vLots[lotIndex] = updatedLot;
    onUpdateConfig(item.id, { ...cfg, variantLots: { ...variantLots, [variantId]: vLots } });
  };
  const handleAddVariantLot = (variantId) => {
    const vLots = variantLots[variantId] || [{ ...EMPTY_LOT }];
    onUpdateConfig(item.id, { ...cfg, variantLots: { ...variantLots, [variantId]: [...vLots, { ...EMPTY_LOT }] } });
  };
  const handleRemoveVariantLot = (variantId, lotIndex) => {
    const vLots = (variantLots[variantId] || []).filter((_, i) => i !== lotIndex);
    onUpdateConfig(item.id, { ...cfg, variantLots: { ...variantLots, [variantId]: vLots.length > 0 ? vLots : [{ ...EMPTY_LOT }] } });
  };

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        borderRadius: '12px !important',
        mb: 2,
        overflow: 'hidden',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '0 0 16px 0' }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 3, py: 0.5,
          bgcolor: alpha(theme.palette.grey[50], 0.8),
          borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.6)}`,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center', gap: 2, my: 1.5
          }
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {item.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {getSummaryText()}
          </Typography>
        </Box>
        <Chip
          label={isComplete ? 'Ready' : 'Incomplete'}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: '0.75rem',
            bgcolor: isComplete
              ? alpha(theme.palette.success.main, 0.1)
              : alpha(theme.palette.warning.main, 0.1),
            color: isComplete
              ? theme.palette.success.main
              : theme.palette.warning.main
          }}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3 }}>
        {/* Variant selector (only if product has variants) */}
        {variants.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Variants
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => onOpenVariantDialog(item.id)}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                minHeight: 42,
                borderRadius: 2,
                backgroundColor: 'white',
                borderColor: selectedVariantIds.length > 0
                  ? alpha(theme.palette.primary.main, 0.4)
                  : alpha(theme.palette.grey[400], 0.8),
                color: selectedVariantIds.length > 0 ? 'text.primary' : 'text.secondary',
                fontWeight: 500,
                px: 2,
                textTransform: 'none',
                fontSize: '0.85rem',
                '&:hover': { borderColor: theme.palette.primary.main }
              }}
            >
              {selectedVariantIds.length > 0
                ? `${selectedVariantIds.length} variant${selectedVariantIds.length > 1 ? 's' : ''} selected`
                : 'Select Variants'}
            </Button>
            {selectedVariantObjects.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {selectedVariantObjects.map(v => (
                  <Chip key={v.id} label={v.name} size="small" sx={{ fontSize: '0.75rem' }} />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── Per-Variant Lot Sections ── */}
        {hasMultiVariants ? (
          <Box>
            {selectedVariantObjects.map((variant) => {
              const vLots = variantLots[variant.id] || [{ ...EMPTY_LOT }];
              const variantTotal = calcLotTotal(vLots);
              return (
                <Paper
                  key={variant.id}
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{
                    px: 2.5, py: 1.5,
                    bgcolor: alpha(theme.palette.grey[100], 0.6),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                        {variant.name}
                      </Typography>
                      {variant.sku && (
                        <Chip label={variant.sku} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 22 }} />
                      )}
                    </Box>
                    {variantTotal > 0 && (
                      <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                        {variantTotal.toLocaleString()} units
                        {vLots.length > 1 && ` · ${vLots.length} lots`}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {vLots.map((lot, idx) => (
                      <ProductLotEntry
                        key={idx}
                        lot={lot}
                        lotIndex={idx}
                        totalLots={vLots.length}
                        onOpenConfigDialog={(lotIdx) => onOpenConfigForLot(variant.id, lotIdx)}
                        onChange={(lotIdx, updatedLot) => handleVariantLotChange(variant.id, lotIdx, updatedLot)}
                        onRemove={(lotIdx) => handleRemoveVariantLot(variant.id, lotIdx)}
                        textFieldSx={textFieldSx}
                      />
                    ))}
                  </Box>

                  <Box sx={{ px: 2.5, pb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddVariantLot(variant.id)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        px: 2.5,
                        py: 0.8,
                        borderStyle: 'dashed',
                        borderColor: alpha(theme.palette.grey[400], 0.8),
                        color: 'text.secondary',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                          borderStyle: 'dashed',
                          bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      Add Another Lot
                    </Button>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          /* ── No Variants: flat lot list ── */
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {flatLots.map((lot, idx) => (
                <ProductLotEntry
                  key={idx}
                  lot={lot}
                  lotIndex={idx}
                  totalLots={flatLots.length}
                  onOpenConfigDialog={(lotIdx) => onOpenConfigForLot(null, lotIdx)}
                  onChange={handleLotChange}
                  onRemove={handleRemoveLot}
                  textFieldSx={textFieldSx}
                />
              ))}
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddLot}
              sx={{
                mt: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                px: 2.5,
                py: 0.8,
                borderStyle: 'dashed',
                borderColor: alpha(theme.palette.grey[400], 0.8),
                color: 'text.secondary',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  borderStyle: 'dashed',
                  bgcolor: alpha(theme.palette.primary.main, 0.04)
                }
              }}
            >
              Add Another Lot
            </Button>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
